import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import {
  ArrowRight,
  Building2,
  CalendarPlus,
  Link2,
  LogIn,
  Megaphone,
  Radio,
  Trash2,
  UserPlus,
  UsersRound,
  type LucideIcon,
} from 'lucide-react'
import { PlayHeadline } from '@/components/brand/PlayHeadline'
import { SiteHeader } from '@/components/SiteHeader'
import { SiteFooter } from '@/components/SiteFooter'
import { HOST_PERKS } from '@/components/hostPerks'
import { ButtonLink, Skeleton, SkeletonGroup } from '@/components/play'
import { isViewer } from '@/components/auth/isViewer'
import { authLink } from '@/components/onboarding/authContinuation'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useAuth } from '@/state/AuthContext'
import '@/components/site-page.css'
import './HostWatchAlongPage.css'

/** How long steps held below the fold wait for IntersectionObserver before they show anyway. */
const ENTRANCE_FALLBACK_MS = 1500

// Today's path, as the app has it. Sign-up names an organization for you; a viewer names one at /start-hosting. Every
// event's public page is the watch screen, so step 4 goes live there as soon as the event exists.
// Upcoming events (/explore) is parked, and with it step 3's listing line: see docs/features/explore.md to restore it.
// Steps 4 to 6 are the watch screen's own controls: Go live, Show my audience on a previewed camera, and the chat's
// Delete, Time out 10 minutes and Remove from chat. Step 7 is the event workspace's Hosts, whose Invite a host (with
// the same UserPlus) makes a single-use link: each host it brings gets a stage and chat of their own. Step 8 is the
// watch screen's guests: a host's Invite on stage on a chat message, or the stage's link (10 people, 24 hours), within
// the stage's four seats, hosts included. UsersRound, the dock's guests glyph, keeps it apart from step 7's UserPlus.
const steps: { icon: LucideIcon; title: string; detail: string }[] = [
  { icon: LogIn, title: 'Sign in', detail: 'Or create an account.' },
  { icon: Building2, title: 'Start hosting', detail: 'Name your organization, if asked.' },
  { icon: CalendarPlus, title: 'Create an event', detail: 'Invite your cameras.' },
  { icon: Radio, title: 'Go live', detail: 'Open its public page. Check your camera and mic.' },
  { icon: Megaphone, title: 'Call the cameras', detail: 'Preview a camera, then choose Show my audience.' },
  { icon: Trash2, title: 'Keep the chat friendly', detail: 'Delete messages, time people out or remove them from chat.' },
  { icon: UserPlus, title: 'Invite co-hosts', detail: 'Choose Invite a host in your event. Each host gets their own stage and chat.' },
  {
    icon: UsersRound,
    title: 'Bring guests on stage',
    detail: 'Invite on stage from chat, or share your stage’s link: up to 10 people, for 24 hours. Four seats a stage, hosts included.',
  },
]

type Entrance = 'still' | 'waiting' | 'entering'

/**
 * Steps that start below the fold wait there and arrive in order once they scroll into view, or after
 * ENTRANCE_FALLBACK_MS if IntersectionObserver hasn't reported at all by then (a page that renders no frames). Steps already in view,
 * reduced motion and a missing IntersectionObserver never wait. The stylesheet hides waiting steps only on screens
 * that allow motion, so they show by default.
 */
function useEntrance() {
  const ref = useRef<HTMLOListElement>(null)
  const [entrance, setEntrance] = useState<Entrance>('still')
  useLayoutEffect(() => {
    const node = ref.current
    if (!node || typeof IntersectionObserver === 'undefined' || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
    if (window.innerHeight > 0 && node.getBoundingClientRect().top >= window.innerHeight) setEntrance('waiting')
  }, [])
  useEffect(() => {
    const node = ref.current
    if (entrance !== 'waiting' || !node) return
    const enter = () => setEntrance('entering')
    // Any report, in view or not, shows the observer works, so the steps wait for it rather than entering off-screen.
    const fallback = window.setTimeout(enter, ENTRANCE_FALLBACK_MS)
    const observer = new IntersectionObserver((entries) => {
      window.clearTimeout(fallback)
      if (entries.some((entry) => entry.isIntersecting)) enter()
    }, { threshold: 0 })
    observer.observe(node)
    return () => {
      observer.disconnect()
      window.clearTimeout(fallback)
    }
  }, [entrance])
  return [ref, entrance] as const
}

/** One next step for each kind of visitor. Nothing shows while the account loads, so a viewer or host never sees the
 *  signed-out set first; the one placeholder stands for the single button either of them gets. Hosts' secondary
 *  Upcoming events (/explore) is parked with that page (docs/features/explore.md). */
function NextStep() {
  const { isAuthenticated, authReady, user, organization } = useAuth()
  if (isAuthenticated && !authReady) {
    return (
      <SkeletonGroup label="Loading your account" className="host-guide__actions">
        <Skeleton shape="button" />
      </SkeletonGroup>
    )
  }
  if (isAuthenticated && !isViewer(organization, user)) {
    return (
      <div className="host-guide__actions">
        <ButtonLink to="/app">Your events <ArrowRight aria-hidden="true" /></ButtonLink>
      </div>
    )
  }
  if (isAuthenticated) {
    return (
      <div className="host-guide__actions">
        <ButtonLink to="/start-hosting">Start hosting <ArrowRight aria-hidden="true" /></ButtonLink>
      </div>
    )
  }
  // A new account already hosts (sign-up names its organization), so it goes straight to the create-event form.
  // Signing in comes back to start hosting, which sends someone who already hosts on to their events.
  return (
    <div className="host-guide__actions">
      <ButtonLink to={authLink('/signup', '/app?create=1')}>Create account <ArrowRight aria-hidden="true" /></ButtonLink>
      <ButtonLink to={authLink('/login', '/start-hosting')} variant="secondary">Sign in</ButtonLink>
    </div>
  )
}

/**
 * Host a watch-along (/host): what a watch-along is, what a host gets, today's steps from signing in to moderating
 * the chat, an honest note on what isn't open yet, and one next step for the visitor, viewer or host. A `.play-site`
 * page on the shared shell (components/site-page.css), as is the parked /explore. Docs:
 * docs/features/host-watch-along.md.
 */
export function HostWatchAlongPage() {
  const [stepList, entrance] = useEntrance()
  useDocumentTitle('Host a watch-along · StreamBridge')

  return (
    <div className="play-theme site-page host-guide play-site">
      <a className="site-page__skip" href="#host-main">Skip to content</a>
      <SiteHeader />
      <main id="host-main" className="site-page__main">
        <div className="site-page__intro">
          <p className="site-page__eyebrow">Host a watch-along</p>
          <h1 className="site-page__title"><PlayHeadline words={['Your stage.', 'Your call.']} /></h1>
          <p className="site-page__lead">A watch-along is your own live stage on a race.</p>
          <NextStep />
        </div>

        <section className="host-guide__section">
          <h2 id="host-perks-title" className="sr-only">What you get</h2>
          {/* role="list" because VoiceOver drops list semantics from an unstyled list. */}
          <ul className="host-perks" role="list" aria-labelledby="host-perks-title">
            {HOST_PERKS.map(({ icon: Icon, label }) => (
              <li key={label} className="host-perks__item">
                <span className="host-guide__glyph"><Icon aria-hidden="true" /></span>
                {label}
              </li>
            ))}
          </ul>
        </section>

        <section className="host-guide__section">
          <h2 id="host-steps-title" className="host-guide__heading">How to host</h2>
          <ol ref={stepList} className="host-steps" role="list" aria-labelledby="host-steps-title" data-entrance={entrance}>
            {steps.map(({ icon: Icon, title, detail }, index) => (
              <li key={title} className="host-step" style={{ '--host-step': index } as CSSProperties}>
                <div className="host-step__marks" aria-hidden="true">
                  <span className="host-guide__glyph"><Icon /></span>
                  <span className="host-step__number">{String(index + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="host-step__title">{title}</h3>
                <p className="host-step__detail">{detail}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="host-guide__section host-notes" aria-labelledby="host-notes-title">
          <h2 id="host-notes-title" className="host-notes__title">Good to know</h2>
          <p className="host-notes__note">
            <Link2 aria-hidden="true" />
            Hosting someone else’s race? Ask its organizer for an invite link.
          </p>
        </section>
      </main>
      <SiteFooter />
    </div>
  )
}
